import axios from "./axios";

/** @typedef {'verra' | 'auditor'} EqReportsKind */

const BASE_PATH = {
  verra: "api/verra-reports-eqs",
  auditor: "api/auditor-reports",
};

export const VERRA_EQ_REPORT_NAMES = [
  "verra_eq1",
  "verra_eq4",
  "verra_eq7",
  "verra_eq9",
  "verra_eq5",
];

export const AUDITOR_REPORT_NAMES = [
  "client_total",
  "full_breakdown",
  "by_state_and_region",
  "by_state_and_region_and_charger_type",
  "private_fleet_by_site",
  "by_state_and_region_1",
  "by_state_and_region_2",
  "sd_vista",
  "project_region_renewable_report",
];

const TERMINAL = new Set(["SUCCESS", "FAILURE", "REVOKED"]);

export function isEqReportsSuccessCode(payload) {
  const c = payload?.Code ?? payload?.code;
  return c === "1000" || c === 1000;
}

function buildQuery(params) {
  const { mp_id, start_date, end_date } = params;
  const search = new URLSearchParams();
  search.set("mp_id", String(mp_id));
  if (start_date != null && start_date !== "") {
    search.set("start_date", start_date);
  }
  if (end_date != null && end_date !== "") {
    search.set("end_date", end_date);
  }
  const q = search.toString();
  return q ? `?${q}` : "";
}

/**
 * GET bundled equation reports (Verra EQ or Auditor).
 * @param {EqReportsKind} kind
 * @param {{ mp_id: number, start_date?: string, end_date?: string }} params
 */
export async function fetchEqReports(kind, params) {
  const base = BASE_PATH[kind];
  if (!base) {
    throw new Error(`Unknown eq report kind: ${kind}`);
  }
  const qs = buildQuery(params);
  const url = qs ? `${base}${qs}` : `${base}`;
  const { data } = await axios.get(url);
  if (!isEqReportsSuccessCode(data)) {
    const msg = data?.message || data?.result || "Failed to load reports";
    const err = new Error(typeof msg === "string" ? msg : JSON.stringify(msg));
    err.responseData = data;
    throw err;
  }
  return data;
}

/**
 * POST queue full cache refresh for the monitoring period
 * @param {EqReportsKind} kind
 * @param {{ mp_id: number }} body
 */
export async function refreshEqCache(kind, body) {
  const base = BASE_PATH[kind];
  if (!base) {
    throw new Error(`Unknown eq report kind: ${kind}`);
  }
  const { data } = await axios.post(base, body);
  if (!isEqReportsSuccessCode(data)) {
    const msg = data?.message || data?.result || "Cache refresh request failed";
    const err = new Error(typeof msg === "string" ? msg : JSON.stringify(msg));
    err.responseData = data;
    throw err;
  }
  return data;
}

export async function getCeleryTaskStatus(taskId) {
  const { data } = await axios.get(`api/celery/task/${taskId}/`);
  return data;
}

/**
 * Bulk task status: POST /api/celery/tasks/ with { task_ids: string[] }.
 * Response is an array of { task_id, status, children, result, info }.
 * @param {string[]} taskIds
 * @returns {Promise<{ task_id: string, status: string, children?: unknown[], result?: unknown, info?: string }[]>}
 */
export async function fetchCeleryTasksBulk(taskIds) {
  const unique = [...new Set((taskIds || []).filter(Boolean))];
  if (unique.length === 0) {
    return [];
  }
  const { data } = await axios.post("api/celery/tasks/", { task_ids: unique });
  if (Array.isArray(data)) {
    return data;
  }
  if (Array.isArray(data?.tasks)) {
    return data.tasks;
  }
  if (Array.isArray(data?.results)) {
    return data.results;
  }
  return [];
}

/**
 * Collect Celery ids from bundled GET response (top-level + per-report).
 * @param {{ task_ids?: string[], reports?: { task_id?: string }[] }} bundlePayload
 * @returns {string[]}
 */
export function collectTaskIdsFromBundle(bundlePayload) {
  const fromTop = Array.isArray(bundlePayload?.task_ids) ? bundlePayload.task_ids : [];
  const fromReports = Array.isArray(bundlePayload?.reports)
    ? bundlePayload.reports.map((r) => r?.task_id).filter(Boolean)
    : [];
  return [...new Set([...fromTop, ...fromReports].filter(Boolean))];
}

/**
 * Map bulk POST /api/celery/tasks/ rows onto the requested id list.
 * Any id missing from the response is treated as non-terminal (still pending).
 * @param {string[]} requestedIds
 * @param {any[]} rows
 */
function alignBulkTaskRows(requestedIds, rows) {
  const byId = Object.fromEntries(
    (rows || []).map((r) => [r?.task_id, r]).filter(([id]) => Boolean(id)),
  );
  return requestedIds.map((id) => {
    const row = byId[id];
    if (row) {
      return row;
    }
    return { task_id: id, status: "PENDING", result: null, info: "Not returned in bulk status yet" };
  });
}

/**
 * Bulk /api/celery/tasks/ does not return full task results. For each row with
 * status SUCCESS, load GET /api/celery/task/{task_id}/ and merge (result, status, info).
 *
 * Useful when you already have a full aligned snapshot (e.g. all bulk rows terminal).
 * @param {any[]} alignedStatuses from alignBulkTaskRows
 */
export async function enrichBulkTasksWithIndividualResults(alignedStatuses) {
  const successIds = alignedStatuses
    .filter((s) => String(s?.status || "").toUpperCase() === "SUCCESS")
    .map((s) => s.task_id)
    .filter(Boolean);
  if (successIds.length === 0) {
    return alignedStatuses;
  }
  const details = await Promise.all(
    successIds.map(async (id) => {
      try {
        const detail = await getCeleryTaskStatus(id);
        return { id, detail };
      } catch (e) {
        return {
          id,
          detail: {
            task_id: id,
            status: "FAILURE",
            result: e?.message || "task_detail_error",
            info: "GET api/celery/task failed",
          },
        };
      }
    }),
  );
  const detailById = Object.fromEntries(details.map(({ id, detail }) => [id, detail]));
  return alignedStatuses.map((row) => {
    const id = row.task_id;
    if (String(row?.status || "").toUpperCase() !== "SUCCESS" || !id) {
      return row;
    }
    const detail = detailById[id];
    if (!detail || typeof detail !== "object") {
      return row;
    }
    return {
      ...row,
      ...detail,
      task_id: id,
    };
  });
}

/**
 * Poll POST /api/celery/tasks/ for only tasks still in flight. When bulk reports
 * SUCCESS for an id, fetch GET /api/celery/task/{id}/, store the merged row, and
 * remove that id from subsequent bulk requests. FAILURE / REVOKED are stored
 * without a detail GET. Original `taskIds` order is preserved in `statuses`.
 * @param {string[]} taskIds
 * @param {{ intervalMs?: number, timeoutMs?: number, maxRounds?: number }} [opts]
 * @returns {Promise<{ ok: boolean, failed: boolean, timeout?: boolean, statuses?: any[] }>}
 */
export async function pollTaskIdsUntilSettled(taskIds, opts = {}) {
  const intervalMs = opts.intervalMs ?? 3000;
  const timeoutMs = opts.timeoutMs ?? 900000;
  const maxRounds = opts.maxRounds;
  const originalOrder = [...new Set((taskIds || []).filter(Boolean))];
  if (originalOrder.length === 0) {
    return { ok: true, failed: false };
  }

  /** @type {Record<string, any>} */
  const resolvedById = {};
  /** @type {Set<string>} */
  let pending = new Set(originalOrder);
  const started = Date.now();
  let round = 0;

  while (pending.size > 0) {
    round++;
    if (maxRounds != null && round > maxRounds) {
      const statuses = originalOrder.map((id) =>
        resolvedById[id] || {
          task_id: id,
          status: "FAILURE",
          result: "max_rounds",
          info: `Stopped after ${maxRounds} polling round(s)`,
        },
      );
      return { ok: false, failed: true, timeout: true, statuses };
    }

    if (Date.now() - started >= timeoutMs) {
      const statuses = originalOrder.map((id) =>
        resolvedById[id] || {
          task_id: id,
          status: "FAILURE",
          result: "timeout",
          info: "Polling timed out before task settled",
        },
      );
      return { ok: false, failed: true, timeout: true, statuses };
    }

    const batchIds = [...pending];
    let rows;
    try {
      rows = await fetchCeleryTasksBulk(batchIds);
    } catch (e) {
      for (const id of batchIds) {
        resolvedById[id] = {
          task_id: id,
          status: "FAILURE",
          result: e?.message || "bulk_poll_error",
          info: "Bulk celery status request failed",
        };
      }
      pending = new Set();
      break;
    }

    const aligned = alignBulkTaskRows(batchIds, rows);

    for (const row of aligned) {
      const id = row.task_id;
      if (!id || !pending.has(id)) {
        continue;
      }
      const st = String(row?.status || "").toUpperCase();

      if (st === "SUCCESS") {
        try {
          const detail = await getCeleryTaskStatus(id);
          const merged = { ...row, ...detail, task_id: id };
          const mergedSt = String(merged?.status || "SUCCESS").toUpperCase();
          if (TERMINAL.has(mergedSt)) {
            resolvedById[id] = merged;
            pending.delete(id);
          }
        } catch (err) {
          resolvedById[id] = {
            task_id: id,
            status: "FAILURE",
            result: err?.message || "task_detail_error",
            info: "GET api/celery/task failed",
          };
          pending.delete(id);
        }
        continue;
      }

      if (TERMINAL.has(st)) {
        resolvedById[id] = row;
        pending.delete(id);
      }
    }

    if (pending.size > 0) {
      await new Promise((r) => setTimeout(r, intervalMs));
    }
  }

  const statuses = originalOrder.map((id) => resolvedById[id]);
  const anyFailure = statuses.some((s) => String(s?.status || "").toUpperCase() !== "SUCCESS");
  return { ok: !anyFailure, failed: anyFailure, statuses };
}